import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import './SavePromptModal.css';

/**
 * Save Prompt Modal Component
 * Allows users to save their prompts to the Mia Platform marketplace
 */
const SavePromptModal = ({
  isOpen,
  onClose,
  onSave,
  promptText = '',
  tenantId = null
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [goal, setGoal] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setName('');
      setDescription('');
      setGoal('');
      setError(null);
      setIsSaving(false);
    }
  }, [isOpen]);

  const handleSave = async () => {
    // Validation
    if (!name.trim()) {
      setError('Please enter a prompt name');
      return;
    }

    if (!description.trim()) {
      setError('Please enter a description');
      return;
    }

    if (!tenantId) {
      setError('No tenant selected. Please select a tenant in Canvas mode.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave({
        name: name.trim(),
        description: description.trim(),
        goal: goal.trim() || description.trim(),
        promptText,
        tenantId
      });
      onClose();
    } catch (err) {
      console.error('Error saving prompt:', err);
      setError(err.message || 'Failed to save prompt');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleSave();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="save-prompt-modal-overlay" onClick={onClose}>
      <div className="save-prompt-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="save-prompt-header">
          <h2 className="save-prompt-title">
            <span className="save-prompt-icon">
              <svg fill="none" height="24" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="24">
                <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
                <polyline points="17 21 17 13 7 13 7 21"/>
                <polyline points="7 3 7 8 15 8"/>
              </svg>
            </span>
            Save Prompt
          </h2>
          <button
            className="save-prompt-close"
            onClick={onClose}
            type="button"
            aria-label="Close"
            disabled={isSaving}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="save-prompt-content">
          <p className="save-prompt-description">
            Save this prompt to your Mia Platform marketplace for future reuse.
          </p>

          {/* Name Input */}
          <div className="save-prompt-field">
            <label htmlFor="prompt-name">
              Prompt Name <span className="required">*</span>
            </label>
            <input
              id="prompt-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g., Code in small steps"
              disabled={isSaving}
              autoFocus
            />
          </div>

          {/* Description Input */}
          <div className="save-prompt-field">
            <label htmlFor="prompt-description">
              Description <span className="required">*</span>
            </label>
            <textarea
              id="prompt-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Use this prompt for AI to code in small steps with user approval"
              disabled={isSaving}
              rows={3}
            />
          </div>

          {/* Goal Input (Optional) */}
          <div className="save-prompt-field">
            <label htmlFor="prompt-goal">
              Goal <span className="optional">(optional)</span>
            </label>
            <textarea
              id="prompt-goal"
              value={goal}
              onChange={(e) => setGoal(e.target.value)}
              placeholder="e.g., To facilitate an incremental coding process..."
              disabled={isSaving}
              rows={2}
            />
          </div>

          {/* Prompt Text Preview */}
          <div className="save-prompt-field">
            <label>Prompt Text Preview</label>
            <div className="prompt-text-preview">
              {promptText || 'No prompt text'}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="save-prompt-error">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="save-prompt-footer">
          <button
            className="save-prompt-cancel"
            onClick={onClose}
            type="button"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="save-prompt-save"
            onClick={handleSave}
            type="button"
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Prompt'}
          </button>
        </div>

        {/* Keyboard hint */}
        <div className="save-prompt-hint">
          Press <kbd>⌘</kbd>/<kbd>Ctrl</kbd> + <kbd>Enter</kbd> to save
        </div>
      </div>
    </div>,
    document.body
  );
};

SavePromptModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  promptText: PropTypes.string,
  tenantId: PropTypes.string
};

export default SavePromptModal;
